import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Empty-state primitive (design-system §7). A centered block: a muted HugeIcons
 * icon, a one-line title, a one-line muted description, and the primary action.
 *
 * This is the ONLY place a `border-dashed` is allowed (§7 / §13) — a dashed
 * outline *means* "nothing here yet / drop zone". Compose:
 *
 *   <Empty>
 *     <EmptyHeader>
 *       <EmptyMedia><Inbox /></EmptyMedia>
 *       <EmptyTitle>No tasks yet</EmptyTitle>
 *       <EmptyDescription>Create your first task to get started.</EmptyDescription>
 *     </EmptyHeader>
 *     <EmptyContent><Button>+ New</Button></EmptyContent>
 *   </Empty>
 */
const Empty = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    role="status"
    className={cn(
      "flex min-h-[12rem] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border px-6 py-12 text-center",
      className,
    )}
    {...props}
  />
));
Empty.displayName = "Empty";

const EmptyHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col items-center gap-1.5", className)}
    {...props}
  />
));
EmptyHeader.displayName = "EmptyHeader";

const EmptyMedia = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    aria-hidden="true"
    className={cn(
      "mb-1 flex h-10 w-10 items-center justify-center text-muted-foreground [&_svg]:h-6 [&_svg]:w-6",
      className,
    )}
    {...props}
  />
));
EmptyMedia.displayName = "EmptyMedia";

const EmptyTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm font-medium text-foreground", className)}
    {...props}
  />
));
EmptyTitle.displayName = "EmptyTitle";

const EmptyDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "max-w-sm text-sm text-muted-foreground",
      className,
    )}
    {...props}
  />
));
EmptyDescription.displayName = "EmptyDescription";

const EmptyContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col items-center gap-2", className)}
    {...props}
  />
));
EmptyContent.displayName = "EmptyContent";

export {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
};
